import { useState, useEffect, useCallback } from 'react'; // Removed useRef
import { Process, ProcessListResponse, ProcessCreate, ProcessUpdate } from '../types/costwand.d';
import { apiClient, ApiError } from '../api/apiClient.ts';

const PAGE_SIZE = 10; // Define page size for consistency

interface UseProcessesResult {
  processes: Process[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  fetchProcesses: (page: number) => Promise<void>; // Changed to required page argument
  handlePageChange: (page: number) => void;
  createProcess: (data: ProcessCreate) => Promise<boolean>;
  updateProcess: (id: number, data: ProcessUpdate) => Promise<boolean>;
  deleteProcess: (id: number) => Promise<boolean>;
}

export const useProcesses = (): UseProcessesResult => {
  const [processes, setProcesses] = useState<Process[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProcesses = useCallback(async (page: number) => { // Made page a required argument
    if (page < 1) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient<ProcessListResponse>(
        `/processes?page=${page}&page_size=${PAGE_SIZE}`,
        { bypassCache: true } // ADDED: Bypass cache for pagination
      );
      setProcesses(response.data);
      setTotalPages(response.pagination.total_pages);
      setTotalItems(response.pagination.total_items);
      // REMOVED: setCurrentPage(response.pagination.page);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Failed to fetch processes: ${err.message}`);
      } else {
        setError('An unknown error occurred while fetching processes.');
      }
      setProcesses([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, []); // Removed currentPage from dependencies

  useEffect(() => {
    // Simplified useEffect: always fetch when currentPage changes
    fetchProcesses(currentPage);
  }, [currentPage, fetchProcesses]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const createProcess = async (data: ProcessCreate): Promise<boolean> => {
    setError(null);
    try {
      // Ensure cost is a number
      const payload = { ...data, cost: parseFloat(data.cost as any) };
      await apiClient<Process>('/processes', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      await fetchProcesses(1); // Trigger refetch via useEffect // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      //setError(err instanceof ApiError ? `Failed to create process: ${err.message}` : 'An unknown error occurred during creation.');
      return false;
    }
  };

  const updateProcess = async (id: number, data: ProcessUpdate): Promise<boolean> => {
    setError(null);
    try {
      // Ensure cost is a number if present
      const payload = { ...data };
      if (payload.cost !== undefined) {
        payload.cost = parseFloat(payload.cost as any);
      }
      await apiClient<Process>(`/processes/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      await fetchProcesses(currentPage); // Re-fetch current page to show updated data // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      //setError(err instanceof ApiError ? `Failed to update process: ${err.message}` : 'An unknown error occurred during update.');
      return false;
    }
  };

  const deleteProcess = async (id: number): Promise<boolean> => {
    if (!window.confirm(`Are you sure you want to delete Process ID: ${id}?`)) return false;
    setError(null);
    try {
      await apiClient<void>(`/processes/${id}`, { method: 'DELETE' });
      const pageToFetch = processes.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      await fetchProcesses(pageToFetch); // Trigger refetch via useEffect // MODIFIED: Added await and bypassCache is handled by apiClient for POST/PUT/DELETE
      return true;
    } catch (err) {
      ////setError(err instanceof ApiError ? `Failed to delete process: ${err.message}` : 'An unknown error occurred during deletion.');
      return false;
    }
  };

  return {
    processes,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    fetchProcesses,
    handlePageChange,
    createProcess,
    updateProcess,
    deleteProcess,
  };
};